import dayjs from "dayjs";

import { getMinutesWithLeadingZero } from "./getMinutesWithLeadingZero";

interface SentTime {
  hours: number;
  minutes: number;
  mode?: "absolute" | "relative";
}

/**
 * Formats sent time based on mode (absolute or relative)
 * @param sentTime - The sent time object with hours, minutes, and optional mode
 * @param actualSentDate - Optional actual sent date for scheduled tasks (shows actual time instead of duration)
 * @returns Formatted time string
 */
export const formatSentTime = (sentTime: SentTime, actualSentDate?: string): string => {
  const minutesWithLeadingZero = getMinutesWithLeadingZero(sentTime.minutes);

  if (sentTime.mode === "relative") {
    // If actualSentDate is provided, show the actual scheduled time
    if (actualSentDate) {
      return dayjs(actualSentDate).format("HH:mm");
    }

    // Relative mode: show as duration (e.g., "2h 15min")
    if (sentTime.hours === 0 && sentTime.minutes === 0) {
      return "Natychmiast";
    }

    const parts: string[] = [];
    if (sentTime.hours > 0) {
      parts.push(`${sentTime.hours}h`);
    }
    if (sentTime.minutes > 0) {
      parts.push(`${sentTime.minutes}min`);
    }

    return parts.join(" ");
  }

  // Absolute mode: show as time (e.g., "14:30")
  return `${sentTime.hours}:${minutesWithLeadingZero}`;
};

/**
 * Formats sent time in long format with full Polish text
 * @param sentTime - The sent time object with hours, minutes, and optional mode
 * @returns Formatted time string in long format
 */
export const formatSentTimeLong = (sentTime: SentTime): string => {
  const minutesWithLeadingZero = getMinutesWithLeadingZero(sentTime.minutes);

  if (sentTime.mode === "relative") {
    if (sentTime.hours === 0 && sentTime.minutes === 0) {
      return "Natychmiast po dodaniu";
    }

    const parts: string[] = [];
    if (sentTime.hours > 0) {
      parts.push(`${sentTime.hours} ${sentTime.hours === 1 ? "godzina" : sentTime.hours < 5 ? "godziny" : "godzin"}`);
    }
    if (sentTime.minutes > 0) {
      parts.push(
        `${sentTime.minutes} ${sentTime.minutes === 1 ? "minuta" : sentTime.minutes < 5 ? "minuty" : "minut"}`,
      );
    }

    return `${parts.join(" ")} po dodaniu`;
  }

  // Absolute mode
  return `${sentTime.hours}:${minutesWithLeadingZero}`;
};
